import { useEffect, useRef } from 'react';
import { Button } from '@aigovops/design-system';

/**
 * Generic confirm dialog for Alerts convert/dismiss actions (PR-P3-2).
 * Same native `<dialog>` + `showModal()` pattern as QuestionnaireDetail's
 * citation dialog (focus trap + backdrop + Escape-to-close for free) —
 * `open` attribute alone gives none of those. Contract C10 (role="dialog"
 * mobile-only) doesn't apply here since this is a real `<dialog>`, not a
 * hand-rolled `role="dialog"` div.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
  danger,
  error,
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
  danger?: boolean;
  error?: string | null;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-label={title}
      onClose={onClose}
      onCancel={onClose}
      // showModal() gives backdrop paint + focus-trap + Escape for free,
      // but NOT click-outside-to-dismiss — that needs an explicit check
      // that the click landed on the dialog element itself (the
      // ::backdrop), not a descendant (ux-adversary Platform High).
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elevated)',
        color: 'var(--text)',
        padding: 'var(--space-4)',
        maxWidth: '26rem',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <strong style={{ fontSize: 'var(--font-size-2xs)' }}>{title}</strong>
      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>{description}</div>
      {error ? (
        <p role="alert" style={{ margin: 0, color: 'var(--critical)', fontSize: 'var(--font-size-3xs)' }}>
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="button"
          variant={danger ? 'danger' : 'primary'}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </div>
      </div>
    </dialog>
  );
}
